import { z } from 'zod'
import { hexColorSchema } from './template-primitives'

/**
 * A workspace's brand colours: its own colour for a topic, tag or classification, kept on the
 * stream config (`streams.branding`) and worn wherever Library and Analytics draw that label.
 */

/** A brand colour as the workspace gives it: `#rrggbb`, kept lower-case. */
const brandColorSchema = hexColorSchema.transform((hex) => hex.toLowerCase())

const BRANDED_MAX = 300
const brandedLabels = z
  .record(z.string().trim().min(1).max(200), brandColorSchema)
  .refine((map) => Object.keys(map).length <= BRANDED_MAX, `At most ${BRANDED_MAX} colours`)

/**
 * A workspace's own colours for its topics, tags and classifications, keyed by the label as the
 * rows carry it (a row's topic is its report section until the cache stamps topic ids, so a topic
 * is matched by name, not id). Each replaces the default tint wherever the stream shows that label
 * — Library's chips, Analytics' series — and anything unnamed keeps its default. `brandColors` is
 * the one lookup; matching ignores case and surrounding spaces.
 */
export const streamBrandingSchema = z.object({
  topics: brandedLabels.default({}),
  tags: brandedLabels.default({}),
  /** A classification's colour, wherever a module or a row draws it. */
  classifications: brandedLabels.default({}),
})
export type StreamBranding = z.infer<typeof streamBrandingSchema>
export type BrandedKind = keyof StreamBranding

export const EMPTY_STREAM_BRANDING: StreamBranding = { topics: {}, tags: {}, classifications: {} }

const brandKey = (label: string) => label.trim().toLowerCase()

/** A label's brand colour lookup, built once per branding: undefined where none is set. */
export function brandColors(
  branding: StreamBranding,
): (kind: BrandedKind, label: string) => string | undefined {
  const mapOf = (labels: Record<string, string>) =>
    new Map(Object.entries(labels).map(([k, v]) => [brandKey(k), v]))
  const maps = {
    topics: mapOf(branding.topics),
    tags: mapOf(branding.tags),
    classifications: mapOf(branding.classifications),
  }
  return (kind, label) => maps[kind].get(brandKey(label))
}
